import {
  Alert,
  Badge,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { useLocationId } from '../hooks/useSeedLocation';
import { enumColor, enumLabel } from '../lib/enums';
import { formatPhone } from '../lib/phone';
import { CUSTOMER_FIELDS, type CustomerRecord } from './customers/customer';

const CUSTOMERS_PATH = '/admin/v1/admin/customers';
const PAGE_SIZE = 50;

interface CustomerPage {
  rows: CustomerRecord[];
  page: number;
  pageSize: 50;
  total: number;
  pageCount: number;
}

/** Fifty people at a time; the server searches the whole roster rather than the page on screen. */
export function CustomersPage() {
  const locationId = useLocationId();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [asked] = useDebouncedValue(search.trim(), 250);
  const [who, setWho] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: changing any server-side filter invalidates the current page
  useEffect(() => setPage(1), [asked, who, locationId]);

  const params = {
    locationId,
    q: asked || undefined,
    who: who || undefined,
    page,
  };
  const queryString = new URLSearchParams(
    Object.entries(params)
      .filter((entry): entry is [string, string | number] => entry[1] !== undefined)
      .map(([key, value]) => [key, String(value)]),
  ).toString();
  const query = useQuery({
    queryKey: listKey(CUSTOMERS_PATH, params),
    queryFn: () => rawApi<CustomerPage>('GET', `${CUSTOMERS_PATH}?${queryString}`),
    enabled: Boolean(locationId),
    placeholderData: (previous) => previous,
  });

  const rows = query.data?.rows ?? [];

  return (
    <>
      <Title order={3} mb="md">
        Customers
      </Title>

      <CrudSection
        title="Add a customer"
        entityNoun="customer"
        description="Add a person the register should know. Free-typed walk-up names still do not create customer records."
        listPath={CUSTOMERS_PATH}
        createPath={CUSTOMERS_PATH}
        defaults={{ locationId }}
        fields={CUSTOMER_FIELDS}
        columns={[]}
        formOnly
      />

      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" align="flex-end" wrap="wrap" mb="md">
          <div>
            <Group gap="xs">
              <Title order={5}>People the bar knows</Title>
              {query.isFetching && !query.isLoading && <Loader size={14} />}
            </Group>
            <Text size="sm" c="dimmed">
              Select a row to see the person, their balance, prices and editable details.
            </Text>
          </div>
          <Group align="flex-end" wrap="wrap">
            <TextInput
              label="Search"
              placeholder="Name, phone or email"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              w={260}
            />
            <Select
              label="Who they are"
              placeholder="Everyone"
              clearable
              value={who}
              onChange={setWho}
              data={[
                { value: 'guest', label: 'Guests' },
                { value: 'staff', label: 'Bar staff' },
                { value: 'owner', label: 'The owner' },
                { value: 'seaHuntStaff', label: 'Dive shop staff' },
                { value: 'seaHuntGuest', label: 'Dive shop guests' },
              ]}
              w={190}
            />
          </Group>
        </Group>

        {query.isError && !query.data ? (
          <Alert color="red" title="Could not load customers">
            Nothing has been lost. Refresh the page to try again.
          </Alert>
        ) : (
          <ScrollArea type="auto" offsetScrollbars>
            <DataTable
              idAccessor="id"
              records={rows}
              fetching={query.isLoading}
              totalRecords={query.data?.total ?? 0}
              recordsPerPage={PAGE_SIZE}
              page={page}
              onPageChange={setPage}
              noRecordsText={asked || who ? 'No customers match that search' : 'Nobody is on file yet'}
              minHeight={rows.length === 0 ? 160 : undefined}
              highlightOnHover
              verticalSpacing="sm"
              striped
              onRowClick={({ record }) => navigate(`/customers/${record.id}`)}
              styles={{ table: { minWidth: 680 } }}
              rowStyle={() => ({ cursor: 'pointer' })}
              columns={[
                {
                  accessor: 'name',
                  title: 'Name',
                  render: (record) => (
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600} c={record.status === 'retired' ? 'dimmed' : undefined}>
                        {record.name}
                      </Text>
                      {record.status === 'retired' && (
                        <Badge color="gray" variant="light">
                          Hidden from register
                        </Badge>
                      )}
                      {record.hasOwnPrices && (
                        <Badge color="teal" variant="light">
                          Own prices
                        </Badge>
                      )}
                    </Group>
                  ),
                },
                {
                  accessor: 'linkKind',
                  title: 'Who they are',
                  render: (record) =>
                    record.linkKind === 'guest' ? (
                      <Text size="sm" c="dimmed">
                        Guest
                      </Text>
                    ) : (
                      <Badge color={enumColor('customerLinkKind', record.linkKind)} variant="light">
                        {enumLabel('customerLinkKind', record.linkKind)}
                      </Badge>
                    ),
                },
                {
                  accessor: 'phone',
                  title: 'Phone',
                  render: (record) => formatPhone(record.phone) || <Text c="dimmed">—</Text>,
                },
                {
                  accessor: 'email',
                  title: 'Email',
                  render: (record) => record.email || <Text c="dimmed">—</Text>,
                },
              ]}
            />
          </ScrollArea>
        )}
      </Paper>
    </>
  );
}
